import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Check, Moon, CalendarDays, CalendarRange } from 'lucide-react';

/**
 * StayTypeSelector — Only shown when the chosen site type supports flat-rate stays.
 * Currently: RV Sites only. Primitive Campsites stay nightly.
 */

export const STAY_OPTIONS = [
  {
    id: 'nightly',
    label: 'Nightly',
    icon: Moon,
    price: 40,
    unit: '/ night',
    duration: null,
    tagline: 'Short getaways under the stars',
  },
  {
    id: 'weekly',
    label: 'Weekly',
    icon: CalendarDays,
    price: 100,
    unit: 'flat · 7 nights',
    duration: 7,
    tagline: 'A full week of slow mornings and quiet skies',
    badge: 'Save $180',
  },
  {
    id: 'monthly',
    label: 'Monthly',
    icon: CalendarRange,
    price: 300,
    unit: 'flat · 30 nights',
    duration: 30,
    tagline: 'Long-term stay — settle in, exhale',
    badge: 'Save $900',
  },
];

export const StayTypeSelector = ({ stayType, onChange }) => {
  return (
    <Card
      data-testid="stay-type-selector-card"
      className="border border-[#B8860B]/30 shadow-xl bg-[#0B132B]"
    >
      <CardHeader>
        <CardTitle className="text-2xl text-[#F4F1EA]">How Long Are You Staying?</CardTitle>
        <p className="text-sm text-[#F4F1EA]/65 mt-1">
          Soft-launch promo rates — pick the option that matches your trip.
        </p>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {STAY_OPTIONS.map((opt) => {
            const Icon = opt.icon;
            const active = stayType === opt.id;
            return (
              <button
                key={opt.id}
                type="button"
                data-testid={`stay-type-${opt.id}`}
                onClick={() => onChange(opt.id)}
                className={`relative text-left rounded-xl border-2 p-5 transition-all duration-300 ${
                  active
                    ? 'border-[#B8860B] bg-[#B8860B]/10 shadow-[0_0_24px_rgba(184,134,11,0.25)]'
                    : 'border-[#B8860B]/20 bg-[#050814] hover:border-[#B8860B]/60'
                }`}
              >
                {opt.badge && (
                  <span className="absolute -top-2.5 right-3 px-2 py-0.5 text-[10px] uppercase tracking-widest rounded-full bg-[#B8860B] text-[#050814] font-semibold">
                    {opt.badge}
                  </span>
                )}
                <div className="flex items-center gap-3 mb-3">
                  <span className="w-10 h-10 rounded-full bg-[#B8860B]/10 border border-[#B8860B]/40 flex items-center justify-center">
                    <Icon className="text-[#D4AF37]" size={18} />
                  </span>
                  <h3 className="font-display text-xl font-semibold text-[#F4F1EA]">
                    {opt.label}
                  </h3>
                  {active && (
                    <span className="ml-auto w-6 h-6 bg-[#B8860B] rounded-full flex items-center justify-center">
                      <Check size={14} className="text-[#050814]" />
                    </span>
                  )}
                </div>
                <div className="mb-2">
                  <span className="text-3xl font-bold text-[#B8860B]">
                    ${opt.price}
                  </span>
                  <span className="text-sm text-[#F4F1EA]/65 ml-1.5">{opt.unit}</span>
                </div>
                <p className="text-sm text-[#F4F1EA]/70 italic">{opt.tagline}</p>
              </button>
            );
          })}
        </div>

        <div className="mt-5 p-3 rounded-md bg-[#050814] border-l-4 border-[#B8860B] text-xs text-[#F4F1EA]/75 leading-relaxed">
          <strong className="text-[#D4AF37]">Metered electricity</strong> is billed
          separately based on your usage during the stay — payable at check-out. All
          stays include water, WiFi, and full ranch access.
        </div>
      </CardContent>
    </Card>
  );
};

export default StayTypeSelector;
